import { Draggable, Droppable } from "@hello-pangea/dnd";
import { isNotEmpty, isPresent } from "neetocist";
import { joinHyphenCase } from "neetocommons/utils/general";
import { Typography } from "neetoui";
import { useTranslation } from "react-i18next";

import Body from "components/Builder/Body";

import SelectedElement from "./SelectedElement";
import TitleBar from "./TitleBar";

import {
  DROPPABLE_TYPES,
  SELECTED_ELEMENTS_LIST_DROPPABLE_ID,
} from "../../constants";

const SelectedElementsList = ({
  handleSelectElement,
  isReorderDisabled,
  isElementVisible,
  selectedElementId,
  selectedElements,
  setVisiblePanel,
  fixedLastElement,
  fixedFirstElement,
  anchoredElementIds,
  selectedElementActions,
  addAllElements,
  isAddingAllElements,
  showElementsFillButton,
  isAddingNewElement,
  addNewElement,
  addNewElementLabel,
  isSelectedElementActionsDisabled,
  selectedElementActionsTooltipProps,
  renderElementsCount,
}) => {
  const renderSelectedElement = (element, index = 0) => (
    <SelectedElement
      {...{
        element,
        handleSelectElement,
        index,
        isSelectedElementActionsDisabled,
        selectedElementActionsTooltipProps,
      }}
      isSelectable
      actions={element.actions}
      isActive={selectedElementId === element.id}
      key={element.id || index}
      data-testid={`welcome-element${
        Array.isArray(fixedFirstElement) ? `-${index + 1}` : ""
      }`}
    />
  );

  const { t } = useTranslation();

  const checkIsVisible = element => isElementVisible?.(element) ?? true;
  const visibleCount =
    typeof renderElementsCount === "function"
      ? (selectedElements ?? []).filter(checkIsVisible).length
      : 0;

  return (
    <>
      <TitleBar
        {...{
          addAllElements,
          addNewElement,
          addNewElementLabel,
          isAddingAllElements,
          isAddingNewElement,
          setVisiblePanel,
          showElementsFillButton,
        }}
      />
      <Body data-testid="selected-elements-container">
        <Typography className="mb-3" style="body3">
          {t("neetoMolecules.builderSidebar.elementsReorderDescription")}
        </Typography>
        {typeof renderElementsCount === "function" &&
          renderElementsCount(visibleCount)}
        {Array.isArray(fixedFirstElement)
          ? isNotEmpty(fixedFirstElement) &&
            fixedFirstElement.map(renderSelectedElement)
          : isPresent(fixedFirstElement) &&
            renderSelectedElement(fixedFirstElement)}
        <Droppable
          droppableId={SELECTED_ELEMENTS_LIST_DROPPABLE_ID}
          type={DROPPABLE_TYPES.SELECTED_ELEMENT}
        >
          {({ innerRef, droppableProps, placeholder }) => (
            <div
              ref={innerRef}
              {...droppableProps}
              data-testid="selected-elements-droppable-container"
            >
              {selectedElements?.map((element, index) => {
                const isVisible = checkIsVisible(element);

                if (!isVisible) return null;

                const isAnchoredElement = anchoredElementIds.includes(
                  element.id
                );

                const isDragDisabled = isReorderDisabled || isAnchoredElement;

                const key =
                  element.id ?? `element-${joinHyphenCase(element.label)}`;

                return (
                  <Draggable
                    {...{ index, isDragDisabled }}
                    draggableId={element.id ?? `element-${index}`}
                    key={key}
                  >
                    {({ innerRef, draggableProps, dragHandleProps }) => (
                      <div
                        ref={innerRef}
                        {...draggableProps}
                        {...dragHandleProps}
                      >
                        <SelectedElement
                          {...{
                            element,
                            handleSelectElement,
                            isSelectedElementActionsDisabled,
                            selectedElementActionsTooltipProps,
                          }}
                          isSelectable
                          actions={selectedElementActions}
                          isActive={selectedElementId === element.id}
                          isDraggable={!isDragDisabled}
                          key={element.id ?? index}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {placeholder}
            </div>
          )}
        </Droppable>
        {isPresent(fixedLastElement) && (
          <SelectedElement
            {...{
              handleSelectElement,
              isSelectedElementActionsDisabled,
              selectedElementActionsTooltipProps,
            }}
            isSelectable
            actions={fixedLastElement.actions}
            data-testid="submit-button-element"
            element={fixedLastElement}
            isActive={selectedElementId === fixedLastElement.id}
          />
        )}
      </Body>
    </>
  );
};

export default SelectedElementsList;
